/**
 * Auth Context
 *
 * Manages authentication state and JWT tokens.
 * After login and on boot, GET /api/me is the source of truth for user + Team Roles.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  apiGet,
  resetSessionExpiredGuard,
} from '@/lib/apiClient';
import { sessionErrorOutcome } from '@/lib/auth/sessionRestore';
import { ErrorState } from '@/components/shared/ErrorState';

export type SessionTeamRole = {
  id: number;
  name: string;
};

export type AuthUser = {
  id: number;
  email: string;
  full_name?: string | null;
  role?: string | null;
  access_level?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  active?: boolean;
};

type MeResponse = {
  user: {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string;
    access_level: number | null;
    active: boolean;
  };
  primaryTeamRole: SessionTeamRole | null;
  additionalTeamRoles: SessionTeamRole[];
  permissions: string[];
};

/** Matches SalesPipeline / legacy admin gate: access_level 1 */
const ACCESS_LEVEL_ADMIN = 1;

interface AuthContextType {
  user: AuthUser | null;
  primaryTeamRole: SessionTeamRole | null;
  additionalTeamRoles: SessionTeamRole[];
  permissions: string[];
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  /** Resolves with the signed-in user (from /api/me) so the caller can route by access level. */
  login: (email: string, password: string, rememberMe?: boolean) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- Context co-located with provider
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_API_SERVER_URL || 'http://localhost:3000';

function getStoredTokens() {
  return {
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  };
}

function storeTokens(accessToken: string, refreshToken?: string) {
  localStorage.setItem('accessToken', accessToken);
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  }
}

function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('sessionRoles');
}

/** Last /me snapshot, used to keep a signed-in user in the app while the API is unreachable. */
function readCachedSession(): {
  user: AuthUser;
  roles: Pick<MeResponse, 'primaryTeamRole' | 'additionalTeamRoles' | 'permissions'> | null;
} | null {
  try {
    const user = JSON.parse(localStorage.getItem('user') ?? 'null') as AuthUser | null;
    if (!user) return null;
    const roles = JSON.parse(localStorage.getItem('sessionRoles') ?? 'null');
    return { user, roles };
  } catch {
    return null;
  }
}

function toAuthUser(me: MeResponse): AuthUser {
  const { user } = me;
  return {
    id: user.id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    full_name: user.full_name,
    access_level: user.access_level,
    active: user.active,
    role: user.access_level != null ? `level_${user.access_level}` : null,
  };
}

function persistSession(me: MeResponse) {
  const authUser = toAuthUser(me);
  localStorage.setItem('user', JSON.stringify(authUser));
  localStorage.setItem(
    'sessionRoles',
    JSON.stringify({
      primaryTeamRole: me.primaryTeamRole,
      additionalTeamRoles: me.additionalTeamRoles,
      permissions: me.permissions,
    }),
  );
  return authUser;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [primaryTeamRole, setPrimaryTeamRole] = useState<SessionTeamRole | null>(null);
  const [additionalTeamRoles, setAdditionalTeamRoles] = useState<SessionTeamRole[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  /** Boot GET /me failed (not a 401) with no cached user: show "Can't reach the server" instead of /signin. */
  const [unreachable, setUnreachable] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const applyMe = useCallback((me: MeResponse) => {
    const authUser = persistSession(me);
    setUser(authUser);
    setPrimaryTeamRole(me.primaryTeamRole);
    setAdditionalTeamRoles(me.additionalTeamRoles);
    setPermissions(me.permissions);
  }, []);

  /** The one sign-out path (logout and session expiry): tokens, state and the react-query cache. */
  const signOutLocally = useCallback(() => {
    clearTokens();
    setAccessToken(null);
    setUser(null);
    setPrimaryTeamRole(null);
    setAdditionalTeamRoles([]);
    setPermissions([]);
    // The next login on this tab must not read the previous user's cached data.
    queryClient.clear();
  }, [queryClient]);

  const fetchMe = useCallback(async () => {
    const me = await apiGet<MeResponse>('/me');
    applyMe(me);
    return me;
  }, [applyMe]);

  /** Revalidate the stored token via GET /api/me (source of truth). */
  const restoreSession = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      try {
        await fetchMe();
        resetSessionExpiredGuard();
        setUnreachable(false);
      } catch (error) {
        if (isCancelled()) return;
        const cached = readCachedSession();
        const outcome = sessionErrorOutcome(error, cached !== null);
        setUnreachable(outcome === 'unreachable');
        if (outcome === 'use_cached' && cached) {
          // API down / 5xx: keep the token; pages show their own "Could not load" + Try Again.
          setUser(cached.user);
          setPrimaryTeamRole(cached.roles?.primaryTeamRole ?? null);
          setAdditionalTeamRoles(cached.roles?.additionalTeamRoles ?? []);
          setPermissions(cached.roles?.permissions ?? []);
        } else if (outcome === 'sign_out') {
          clearTokens();
          setAccessToken(null);
          setUser(null);
          setPrimaryTeamRole(null);
          setAdditionalTeamRoles([]);
          setPermissions([]);
        }
      }
    },
    [fetchMe],
  );

  /** Boot: if a token exists, revalidate it. */
  useEffect(() => {
    let cancelled = false;

    async function init() {
      const { accessToken: storedToken } = getStoredTokens();
      if (!storedToken) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      setAccessToken(storedToken);
      await restoreSession(() => cancelled);
      if (!cancelled) setIsLoading(false);
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, [restoreSession]);

  useEffect(() => {
    const onSessionExpired = () => {
      signOutLocally();
      toast.error('Your session has expired. Please sign in again.');
      navigate('/signin', { replace: true });
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [navigate, signOutLocally]);

  const login = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      try {
        const response = await fetch(`${API_URL}/api/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password, rememberMe }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Login failed');
        }

        const data = await response.json();

        storeTokens(data.data.accessToken, data.data.refreshToken);
        setAccessToken(data.data.accessToken);
        resetSessionExpiredGuard();

        /** Source of truth: roles come from /me, not the thin login payload */
        const me = await fetchMe();
        return toAuthUser(me);
      } catch (error) {
        clearTokens();
        setAccessToken(null);
        setUser(null);
        setPrimaryTeamRole(null);
        setAdditionalTeamRoles([]);
        setPermissions([]);
        throw error;
      }
    },
    [fetchMe],
  );

  const logout = useCallback(async () => {
    try {
      if (accessToken) {
        await fetch(`${API_URL}/api/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      signOutLocally();
      navigate('/signin');
    }
  }, [accessToken, navigate, signOutLocally]);

  const refreshTokenFunc = useCallback(async () => {
    const { refreshToken: storedRefreshToken } = getStoredTokens();

    if (!storedRefreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${API_URL}/api/refresh-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      storeTokens(data.data.accessToken, data.data.refreshToken);
      setAccessToken(data.data.accessToken);
      await fetchMe();
    } catch (error) {
      await logout();
      throw error;
    }
  }, [logout, fetchMe]);

  const isAdmin = Number(user?.access_level) === ACCESS_LEVEL_ADMIN;

  const value: AuthContextType = {
    user,
    primaryTeamRole,
    additionalTeamRoles,
    permissions,
    accessToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    isAdmin,
    login,
    logout,
    refreshToken: refreshTokenFunc,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {unreachable ? (
        <main className="mx-auto flex min-h-screen max-w-md items-center px-4">
          <ErrorState
            className="w-full"
            title="Can't reach the server."
            message="Check your connection, then try again."
            onRetry={() => void restoreSession()}
            retryLabel="Try Again"
          />
        </main>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return {
    ...context,
    loading: context.isLoading,
    userRole: context.user?.role || null,
  };
}

/** Alias for consumers that import role helpers from contexts/index */
// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export function useUserRoleFromContext() {
  const { userRole, isAdmin, loading } = useAuth();
  return { userRole, isAdmin, loading };
}
